import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UsersList as UsersListComponent } from '@/components/user-management/UsersList';
import { RolesPermissions } from '@/components/user-management/RolesPermissions';
import { UserInviteDialog } from '@/components/user-management/UserInviteDialog';
import { useUsers, useRoles } from '@/hooks/react-query/useUsers';
import { DataLoader } from '@/components/ui/DataLoader';

export default function UserManagementPage() {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useUsers();
  const {
    data: roles = [],
    isLoading: rolesLoading,
    error: rolesError,
  } = useRoles();

  const isLoading = usersLoading || rolesLoading;
  const error = usersError || rolesError;

  const handleUserInvited = () => {
    setInviteDialogOpen(false);
    refetchUsers(); // Refresh the user list
  };

  const handleUserUpdated = () => {
    refetchUsers(); // Refresh the user list
  };

  return (
    <DataLoader
      data={{ users, roles }}
      isLoading={isLoading}
      error={error}
      loadingComponent={
        <div className="max-w-7xl mx-auto p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-64"></div>
            <div className="h-4 bg-muted rounded w-96"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      }
    >
      {() => (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Page Header */}
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                User Management
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage team members and their platform access.
              </p>
            </div>
            <Button
              onClick={() => setInviteDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Invite User
            </Button>
          </div>

          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>
                    View and manage all team members, their roles, and access
                    permissions.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <UsersListComponent
                    users={users}
                    roles={roles}
                    onUserUpdated={handleUserUpdated}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Roles & Permissions Tab */}
            <TabsContent value="roles" className="space-y-6">
              <RolesPermissions roles={roles} />
            </TabsContent>
          </Tabs>

          {/* Invite User Dialog */}
          <UserInviteDialog
            open={inviteDialogOpen}
            onOpenChange={setInviteDialogOpen}
            roles={roles}
            onUserInvited={handleUserInvited}
          />
        </div>
      )}
    </DataLoader>
  );
}
